/**
 * Emoji replacement — card #52.
 *
 * "Claude, also add emjoy replacements where possible." Two vocabularies, for
 * the two ways people already type an emoji without meaning to type a colon:
 *
 *  - **Emoticons** — `:)` becomes 🙂. What Brad typed in the comment that asked
 *    for this, which is as good an argument as any.
 *  - **Shortcodes** — `:tada:` becomes 🎉. What people carry over from Slack.
 *
 * Shared rather than living in the editor because the vocabulary is a product
 * decision, not a rendering one: the same table is what a Slack-shaped
 * shortcode has to mean in a comment, in a description and anywhere else a
 * field learns to accept one. It is also the reason this is unit-testable
 * without a browser.
 *
 * Deliberately a **closed, small** table. An emoji picker is a different
 * feature (#42 made the same call for reactions and settled on eight), and a
 * thousand-entry shortcode list turns every `:` someone types into a hazard.
 * The eight reaction emoji are all reachable here, so the two features speak
 * the same language.
 */
/**
 * Shortcode → emoji. Keys are matched between colons, lowercase.
 *
 * Where a shortcode is one of #42's eight reactions it is spelled the way
 * Slack spells it, so muscle memory carries over.
 */
export const EMOJI_SHORTCODES = {
    // The #42 reaction set, first.
    '+1': '👍',
    thumbsup: '👍',
    '-1': '👎',
    thumbsdown: '👎',
    tada: '🎉',
    smile: '😄',
    eyes: '👀',
    heart: '❤️',
    rocket: '🚀',
    confused: '😕',
    // The rest of what actually turns up in a work thread.
    white_check_mark: '✅',
    heavy_check_mark: '✔️',
    x: '❌',
    warning: '⚠️',
    bug: '🐛',
    fire: '🔥',
    sparkles: '✨',
    wrench: '🔧',
    hammer: '🔨',
    construction: '🚧',
    bulb: '💡',
    memo: '📝',
    book: '📖',
    chart: '📈',
    clock: '⏰',
    hourglass: '⏳',
    lock: '🔒',
    key: '🔑',
    package: '📦',
    ship: '🚢',
    wave: '👋',
    pray: '🙏',
    clap: '👏',
    muscle: '💪',
    ok_hand: '👌',
    point_right: '👉',
    thinking: '🤔',
    grin: '😁',
    laughing: '😆',
    wink: '😉',
    slightly_smiling_face: '🙂',
    neutral_face: '😐',
    cry: '😢',
    sob: '😭',
    scream: '😱',
    sunglasses: '😎',
    party: '🥳',
    coffee: '☕',
    beer: '🍻',
    pizza: '🍕',
    cake: '🎂',
    star: '⭐',
    zap: '⚡',
    boom: '💥',
    skull: '💀',
    ghost: '👻',
    robot: '🤖',
    alien: '👽',
    poop: '💩',
    '100': '💯',
};
/**
 * Emoticon → emoji.
 *
 * Ordered longest-first when they are built into a pattern, so `:-)` is not
 * matched as `:` plus junk and `<3` is not eaten by something shorter.
 */
export const EMOTICONS = {
    ':)': '🙂',
    ':-)': '🙂',
    '=)': '🙂',
    ':D': '😀',
    ':-D': '😀',
    ':(': '🙁',
    ':-(': '🙁',
    ':|': '😐',
    ':-|': '😐',
    ';)': '😉',
    ';-)': '😉',
    ':P': '😛',
    ':-P': '😛',
    ':p': '😛',
    ':/': '😕',
    ':-/': '😕',
    ':o': '😮',
    ':O': '😮',
    ':-O': '😮',
    ":'(": '😢',
    ':*': '😘',
    'B)': '😎',
    '8)': '😎',
    '<3': '❤️',
    '</3': '💔',
    ':+1:': '👍',
    ':-1:': '👎',
};
const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * The emoticons as one alternation, longest first.
 *
 * Longest-first matters: with `:)` ahead of `:-)`, the string `:-)` would match
 * nothing at all, because the alternation would try `:)` at the `:`, fail on
 * the `-`, and move on.
 */
const EMOTICON_ALTERNATION = Object.keys(EMOTICONS)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
/**
 * An emoticon that has just been completed by a space, at a word boundary.
 *
 * The leading group keeps the emoticon anchored the way `#1042` references are
 * (`CARD_REF_INPUT` in the editor's extensions): `http://x` must not have its
 * `:/` rewritten, and neither must the `:)` at the end of `foo:)`. It is
 * excluded from the replaced range, so the character before it survives.
 */
export const EMOTICON_INPUT = new RegExp(`(^|[\\s([{])(${EMOTICON_ALTERNATION})[ ]$`);
/** The same shape, anywhere inside a pasted run. */
export const EMOTICON_PASTE = new RegExp(`(^|[\\s([{])(${EMOTICON_ALTERNATION})(?=[\\s.,!?)\\]}]|$)`, 'g');
/** `:shortcode:` — closed by the second colon, which is what triggers it. */
export const SHORTCODE_INPUT = /(^|[\s([{]):([a-z0-9_+-]{1,32}):$/;
/** The same, anywhere inside a pasted run. */
export const SHORTCODE_PASTE = /(^|[\s([{]):([a-z0-9_+-]{1,32}):(?=[\s.,!?)\]}]|$)/g;
/** The emoji a shortcode names, or null when it names nothing we know. */
export function emojiForShortcode(code) {
    return EMOJI_SHORTCODES[code.toLowerCase()] ?? null;
}
/**
 * The emoji an emoticon draws, or null.
 *
 * Case-sensitive, unlike shortcodes: `:P` and `:p` are both a tongue but `B)`
 * is sunglasses and `b)` is a list item.
 */
export function emojiForEmoticon(emoticon) {
    return EMOTICONS[emoticon] ?? null;
}
/**
 * Every replacement in a string, applied at once.
 *
 * The editor does this a keystroke at a time through ProseMirror input rules;
 * this is the same table applied to text that arrived all in one piece, and is
 * what the tests exercise.
 */
export function replaceEmoji(text) {
    return text
        .replace(SHORTCODE_PASTE, (whole, lead, code) => {
        const emoji = emojiForShortcode(code);
        return emoji === null ? whole : `${lead}${emoji}`;
    })
        .replace(EMOTICON_PASTE, (whole, lead, emoticon) => {
        const emoji = emojiForEmoticon(emoticon);
        return emoji === null ? whole : `${lead}${emoji}`;
    });
}
//# sourceMappingURL=emoji.js.map